import {
    type CSSProperties,
    type KeyboardEvent as ReactKeyboardEvent,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { MenuItem } from './sidebarAccess';
import './SidebarFlyout.css';

interface SidebarFlyoutProps {
    item: MenuItem;
    anchorElement: HTMLButtonElement;
    activePath: string;
    onDismiss: () => void;
    onNavigate: (path: string) => void;
}

interface FlyoutPosition {
    left: number;
    top: number;
}

const readCssLength = (name: string): number => {
    const value = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(name),
    );
    return Number.isFinite(value) ? value : 0;
};

export const SidebarFlyout = ({
    item,
    anchorElement,
    activePath,
    onDismiss,
    onNavigate,
}: SidebarFlyoutProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<FlyoutPosition>(() => {
        const anchor = anchorElement.getBoundingClientRect();
        return { left: anchor.right, top: anchor.top };
    });

    useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;

        const anchor = anchorElement.getBoundingClientRect();
        const inset = readCssLength('--sidebar-inset');
        const panelHeight = panel.getBoundingClientRect().height;
        const maximumTop = Math.max(inset, window.innerHeight - panelHeight - inset);

        setPosition({
            left: anchor.right,
            top: Math.min(Math.max(anchor.top, inset), maximumTop),
        });
        panel.querySelector<HTMLButtonElement>('.sidebar-flyout__item')?.focus();
    }, [anchorElement, item.key]);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (panelRef.current?.contains(target) || anchorElement.contains(target)) return;
            onDismiss();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            onDismiss();
            anchorElement.focus();
        };
        const handleResize = () => onDismiss();

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleResize);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
        };
    }, [anchorElement, onDismiss]);

    if (!item.children?.length) return null;

    const activeChildPath = item.children.reduce<string | undefined>((match, child) => {
        const matches = activePath === child.path || activePath.startsWith(`${child.path}/`);
        if (!matches || (match && match.length >= child.path.length)) return match;
        return child.path;
    }, undefined);

    const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

        const items = Array.from(
            panelRef.current?.querySelectorAll<HTMLButtonElement>('.sidebar-flyout__item') ?? [],
        );
        if (!items.length) return;

        event.preventDefault();
        const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
        if (event.key === 'Home') {
            items[0].focus();
            return;
        }
        if (event.key === 'End') {
            items[items.length - 1].focus();
            return;
        }

        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = currentIndex < 0
            ? 0
            : (currentIndex + direction + items.length) % items.length;
        items[nextIndex].focus();
    };

    const style = {
        '--sidebar-flyout-left': `${position.left}px`,
        '--sidebar-flyout-top': `${position.top}px`,
    } as CSSProperties;

    return createPortal(
        <div
            ref={panelRef}
            className="sidebar-flyout"
            role="menu"
            aria-label={`${item.label} navigation`}
            style={style}
            onKeyDown={handleMenuKeyDown}
        >
            <div className="sidebar-flyout__header">
                <span className="sidebar-flyout__header-icon" aria-hidden="true">
                    {item.icon}
                </span>
                <span>
                    <strong>{item.label}</strong>
                    <small>{item.children.length} destinations</small>
                </span>
            </div>
            <div className="sidebar-flyout__items">
                {item.children.map(child => {
                    const isActive = activeChildPath === child.path;
                    return (
                        <button
                            key={child.path}
                            className={`sidebar-flyout__item ${isActive ? 'sidebar-flyout__item--active' : ''}`}
                            type="button"
                            role="menuitem"
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => onNavigate(child.path)}
                        >
                            <span className="sidebar-flyout__item-icon" aria-hidden="true">
                                {child.icon}
                            </span>
                            <span>{child.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>,
        document.body,
    );
};

export default SidebarFlyout;
